import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import dayjs from "dayjs";
import { CloudArrowUp, Folders } from "@phosphor-icons/react";
import MatterProcessingStatus from "../MatterProcessingStatus/MatterProcessingStatus";
import MatterActivity from "./MatterActivity";
import MatterCollectionsCard from "./MatterCollectionsCard";
import { resolveEntityNameOr } from "../../utils/matterFields";
import { getFileIconBySize } from "../../utils/fileUtils";

/**
 * Matter home (Matthew, 2026-07-22): the middle window's default view for a
 * matter. Navigation lives in the left tree; this surface answers "where is
 * this matter at" — identity, AI readiness, what changed recently — and
 * hosts upload. Folder clicks still open the classic table view, which
 * keeps every table row action available while we iterate.
 */

const Wrap = styled.div`
  padding: 20px 28px 40px;
  overflow-y: auto;
  height: 100%;
`;

const HeaderCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px 18px;
  margin-bottom: 14px;
`;

const MatterName = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const StatusChip = styled.span`
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (p.$open ? "#e7f6ec" : "#f3f4f6")};
  color: ${(p) => (p.$open ? "#177a3e" : "#6b7280")};
`;

const MetaRow = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  color: #5a6c87;
  font-size: 13px;

  b {
    color: #374151;
    font-weight: 600;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(340px, 3fr) minmax(260px, 2fr);
  gap: 18px;
  align-items: start;

  /* A grid item otherwise refuses to get narrower than its contents, so the
     button row used to paint out of the card and across the file list. */
  > * {
    min-width: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

// Exported so sibling dashboard cards (e.g. MatterCollectionsCard) match
// this look without redefining it.
export const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px 16px;
`;

export const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
`;

const RecentRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f4f6fa;
  }
`;

const RecentName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f2937;
  font-size: 13.5px;
`;

const RecentDate = styled.span`
  color: #8a93a3;
  font-size: 12px;
  white-space: nowrap;
`;

export const EmptyNote = styled.div`
  color: #8a93a3;
  font-size: 13px;
  padding: 6px 2px;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;

  > * {
    min-width: 0;
  }
`;

/* The card is a narrow column on a laptop (folder tree + recent files take
   the rest of a 16" screen). Four labels do not fit on one line there.
   Wide cards keep a single row; a narrow card stacks them. */
const UploadActions = styled.div`
  margin-top: 10px;
  max-width: 100%;
  min-width: 0;
  container-type: inline-size;

  .doc-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  @container (max-width: 720px) {
    && .doc-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .doc-actions > .ant-btn,
    .doc-actions > div > button {
      width: 100%;
      height: 40px;
      min-height: 40px;
      flex-shrink: 0;
      box-sizing: border-box;
      padding: 0 16px;
      line-height: 1.2;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
    }

    .doc-actions > div {
      width: 100%;
      flex-shrink: 0;
    }
  }
`;

const DropZone = styled.div`
  border: 1.5px dashed #b8c4d6;
  border-radius: 8px;
  padding: 22px 16px;
  text-align: center;
  color: #5a6c87;
  font-size: 13px;
  background: ${(p) => (p.$active ? "#eef4ff" : "#fafbfd")};
`;

const BrowseLink = styled.button`
  border: none;
  background: none;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

// Flatten the raw matter tree (nested folders[]/documents[]) into a single
// document list, remembering each document's containing folder name.
export const flattenTreeDocuments = (tree) => {
  const out = [];
  const visitDocs = (docs, folderName) => {
    for (const doc of Array.isArray(docs) ? docs : []) {
      if (doc && doc.id != null) out.push({ ...doc, __folderName: folderName });
    }
  };
  const visitFolders = (folders) => {
    for (const folder of Array.isArray(folders) ? folders : []) {
      if (!folder || typeof folder !== "object") continue;
      visitDocs(folder.documents, folder.name || "");
      visitFolders(
        Array.isArray(folder.subfolders) ? folder.subfolders : folder.folders
      );
    }
  };
  visitDocs(tree?.documents, "");
  visitFolders(tree?.folders);
  return out;
};

const docTimestamp = (doc) =>
  doc?.last_modified || doc?.uploaded_at || doc?.time_created || null;

export const selectRecentDocuments = (tree, limit = 10) =>
  flattenTreeDocuments(tree)
    .map((doc) => ({ doc, ts: docTimestamp(doc) }))
    .filter((x) => x.ts)
    .sort((a, b) => new Date(b.ts) - new Date(a.ts))
    .slice(0, limit)
    .map((x) => x.doc);

const MatterDashboard = ({
  matterId,
  matterDetails,
  matterTree,
  onOpenDocument,
  onBrowseFiles,
  uploadSlot,
  onDropFiles,
  isDropActive,
  onDragStateChange,
}) => {
  const recent = useMemo(() => selectRecentDocuments(matterTree), [matterTree]);
  const isOpen = matterDetails?.status === "Open";

  return (
    <Wrap data-testid="matter-dashboard">
      <HeaderCard>
        <MatterName>
          {matterDetails?.matter_displayName || "Matter"}
        </MatterName>
        {matterDetails?.status && (
          <StatusChip $open={isOpen}>{matterDetails.status}</StatusChip>
        )}
        <MetaRow>
          <span>
            <b>Case type:</b> {resolveEntityNameOr(matterDetails?.case_type)}
          </span>
          <span>
            <b>Case manager:</b>{" "}
            {resolveEntityNameOr(matterDetails?.case_manager)}
          </span>
          <span>
            <b>Attorney:</b>{" "}
            {resolveEntityNameOr(matterDetails?.principal_attourney)}
          </span>
        </MetaRow>
      </HeaderCard>

      <MatterProcessingStatus matter={matterDetails} />

      <SectionGrid>
        <Card>
          <CardTitle>Recent documents</CardTitle>
          {recent.length === 0 ? (
            <EmptyNote>
              No documents yet — drop files on the right to get started.
            </EmptyNote>
          ) : (
            recent.map((doc) => (
              <RecentRow
                key={doc.unique_id || doc.id}
                type="button"
                onClick={() => onOpenDocument && onOpenDocument(doc)}
                title={doc.name}
              >
                {getFileIconBySize(doc.name, "document", 18)}
                <RecentName>{doc.name}</RecentName>
                {doc.__folderName ? (
                  <RecentDate>{doc.__folderName}</RecentDate>
                ) : null}
                <RecentDate>
                  {dayjs(docTimestamp(doc)).format("MMM D, h:mm A")}
                </RecentDate>
              </RecentRow>
            ))
          )}
        </Card>

        <SideColumn>
          <Card
            onDrop={onDropFiles}
            onDragOver={(e) => {
              e.preventDefault();
              onDragStateChange && onDragStateChange(true);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget)) return;
              onDragStateChange && onDragStateChange(false);
            }}
          >
            <CardTitle>Add documents</CardTitle>
            <DropZone $active={!!isDropActive}>
              <CloudArrowUp size={26} weight="regular" />
              <div style={{ marginTop: 6 }}>
                Drag files here to upload to this matter
              </div>
            </DropZone>
            {uploadSlot ? (
              <UploadActions data-testid="upload-actions">{uploadSlot}</UploadActions>
            ) : null}
          </Card>

          <Card>
            <CardTitle>All files</CardTitle>
            <BrowseLink type="button" onClick={onBrowseFiles}>
              <Folders size={16} weight="regular" />
              Browse the full folder listing
            </BrowseLink>
            <EmptyNote style={{ marginTop: 6 }}>
              Or navigate with the folder tree on the left — documents open as
              tabs above.
            </EmptyNote>
          </Card>

          {matterId ? <MatterCollectionsCard matterId={matterId} /> : null}
        </SideColumn>
      </SectionGrid>

      {/* Full width beneath the grid: these are tables, and squeezing them
          into a dashboard column would truncate every column that matters. */}
      {matterId ? <MatterActivity matterId={matterId} /> : null}
    </Wrap>
  );
};

MatterDashboard.propTypes = {
  matterId: PropTypes.string,
  matterDetails: PropTypes.object,
  matterTree: PropTypes.object,
  onOpenDocument: PropTypes.func,
  onBrowseFiles: PropTypes.func,
  uploadSlot: PropTypes.node,
  onDropFiles: PropTypes.func,
  isDropActive: PropTypes.bool,
  onDragStateChange: PropTypes.func,
};

export default MatterDashboard;
